import { Badge, Card, Grid, Group, Text } from "@mantine/core"
import { createColumnHelper } from "@tanstack/react-table"
import React from "react"
import { Link, useLocation, useNavigate } from "react-router-dom"
import { AgencyOperationsAdminVenueTabs } from "src/components/agency/operations/administrator/venues/AgencyOperationsAdminVenueTabs"
import { CreateAgencyOperationsAdminVenueAreaAction } from "src/components/agency/operations/administrator/venues/areas/AgencyOperationsAdminVenueAreaActions"
import { DataTable } from "src/components/ui/DataTable"
import { LoadMoreButton } from "src/components/ui/LoadMoreButton"
import { EmptyPageComponent } from "src/components/ui/Misc"
import { ModuleErrors } from "src/components/ui/ModuleErrors"
import { PageFilters, usePageFilters } from "src/components/ui/PageFilters"
import { PageHeader } from "src/components/ui/PageHeader"
import { PageLayout } from "src/components/ui/PageLayout"
import { useActions, useAppState } from "src/state"
import { SetMetaInput } from "src/state/Module"
import { AreaFragment, Area_Status_Enum, Areas_Bool_Exp } from "src/state/gql/_types"
import { formatDate } from "src/utils/dates"
import { capitalizeWord } from "src/utils/string"

export function AgencyOperationsAdminVenueAreas() {

    const navigate = useNavigate()
    const location = useLocation();
    const { itemsList, errors, meta, loadingMore, loading, meta: { limit, offset, sortKey, sortOrder } } = useAppState(state => state.areas)
    const { 
        subscribeToItems, 
        unsubscribeFromItems, 
        getMoreItems, 
        resetModule, 
        setMeta,
    } = useActions().areas
    const { view } = useAppState(state => state.ui)
    const { currentItemId: currentTenatId } = useAppState(state => state.tenants)

    const {
        filters,
        filterParams
    } = usePageFilters<Areas_Bool_Exp['_and'], AreaFragment>({
        baseFilters: [{
            venue_id: { _eq: currentTenatId }
        }],
        clickFilters: [
            {
                _or: [
                    { status: { _eq: Area_Status_Enum.Active } }
                ]
            }
        ],
        searchFilterFields: [],
        searchFilterRelations: {},
        clickFilterField: 'status',
        clickFiltersDisableDefaultAll: true,
        clickFilterDefault: 'active',
        clickFilterValues: {
            active: { label: 'Active', filter: [{ status: { _eq: Area_Status_Enum.Active } }] },
            inactive: { label: 'Inactive', filter: [{ status: { _eq: Area_Status_Enum.Inactive } }] },
            all: { label: 'All', filter: [] }
        }
    })

    React.useEffect(() => {
        subscribeToItems(filters);
        return () => {
            unsubscribeFromItems()
        }
    }, [filters, limit, offset, sortKey, sortOrder, subscribeToItems, unsubscribeFromItems, resetModule])

    const columnHelper = createColumnHelper<AreaFragment>()

    const columns = React.useMemo(() => [
        columnHelper.accessor('id', {
            header: 'Id'
        }),
        columnHelper.accessor('area_number', {
            header: '#'
        }),
        columnHelper.accessor(row => row.area_type.display, {
            id: 'type',
            meta: {
                relationKey: 'area_type',
                sortKey: 'value'
            },
            header: 'Type'
        }),
        columnHelper.accessor('status', {
            header: 'Status',
            cell: row => capitalizeWord(row.getValue())
        }),
        columnHelper.accessor('name', {
            header: 'Name'
        }),
        columnHelper.accessor('poc', {
            header: 'Poc'
        }),
        columnHelper.accessor('created_at', {
            header: 'Created At',
            cell: row => formatDate(row.getValue(), 'MM/DD/YY')
        }),
        columnHelper.accessor('updated_at', {
            header: 'Updated At',
            cell: row => formatDate(row.getValue(), 'MM/DD/YY')
        }),
    ], [columnHelper])

    const initialState = {
        columnVisibility: {
            id: false
        }
    }

    const handleMetaChange = (meta: SetMetaInput<AreaFragment>) => {
        setMeta(meta)
    }

    const handleSelect = (id: string) => {
        navigate(id, { state: { drawerFrom: location } })
    }

    const renderCards = () => {
        return (
            <React.Fragment>
                <Grid>
                    {itemsList.map((i, k) => {
                        return (
                            <Grid.Col span={6} key={k}>
                                <Card
                                    component={Link}
                                    to={`/areas/${i.id}`}
                                    shadow="xs"
                                    className="card-theme-global"
                                >
                                    <Group justify='space-between'>
                                        <Text fw={500}>#{i.id}</Text>
                                        <Badge variant="outline">{i.status}</Badge>
                                    </Group>
                                    <Text size="sm">{i.id}</Text>
                                </Card>
                            </Grid.Col>
                        )
                    })}
                </Grid>
                <LoadMoreButton meta={meta} onLoadMore={getMoreItems} loading={loadingMore} />
            </React.Fragment>
        )
    }

    const renderList = () => {
        return (
            <DataTable<AreaFragment>
                columns={columns}
                data={itemsList}
                loading={loading}
                meta={meta}
                onMetaChange={handleMetaChange}
                initialState={initialState}
                onSelectItem={handleSelect}
            />
        )
    }

    return (
        <PageLayout
            header={
                <PageHeader
                    title={`Areas`}
                    renderAction={
                        <Group>
                            <CreateAgencyOperationsAdminVenueAreaAction/>
                        </Group>
                    }
                />
            }
        >
            <AgencyOperationsAdminVenueTabs/>
            <PageFilters options={filterParams} />
            <ModuleErrors errors={errors} />
            {itemsList.length ? (<> {view === 'list' ? renderList() : renderCards()} </>) : (<EmptyPageComponent label='Areas' />)}
        </PageLayout>
    )

}